// eslint-disable-next-line import/no-cycle
import {
  OperatorType,
  TemplatesElementsHistory,
  TemplatesElementsHistoryGroup,
  TemplatesElementsHistoryGroupInputItem,
  TemplatesElementsHistoryInputItem,
} from '@/types/generated-types/graphql';

// eslint-disable-next-line import/no-cycle
import { store } from '@/store';

// eslint-disable-next-line import/no-cycle
import {
  getSectionIdsFromIframe,
  getSectionIdsFromTemplate,
  getLastActiveSection,
  markTemplateAsEdited,
  replaceSectionContent,
  setTemplateConfigurationKeyInState,
  TemplateEditorState as state,
  Template,
  updateSectionsInState,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { Section } from '@/types/template-builder-types';
// eslint-disable-next-line import/no-cycle
import { HistoryType } from '@/types';
import { computed, ComputedRef } from 'vue';
// eslint-disable-next-line import/no-cycle
import {
  Insert,
  List,
} from '../GraphQL';
// eslint-disable-next-line import/no-cycle
import { UserState } from '../User';

export const HISTORY_PAGE_SIZE = 20;

// Track templates that have unsaved changes to avoid redundant history creation on save
const templatesWithUnsavedChanges = new Set<number>();

export const markTemplateAsChanged = (templateId: number) => {
  templatesWithUnsavedChanges.add(templateId);
};

// Translation edits are not history steps: flag them so the next save point captures them
export const markTranslationsAsChanged = () => {
  markTemplateAsEdited();
  if (state.template) {
    markTemplateAsChanged(state.template.id);
  }
};

export const hasTemplateUnsavedChanges = (templateId: number): boolean => templatesWithUnsavedChanges.has(templateId);

export const clearTemplateUnsavedChanges = (templateId: number) => {
  templatesWithUnsavedChanges.delete(templateId);
};

export interface HistoryPaginationResult {
  items: TemplatesElementsHistoryGroup[];
  hasMore: boolean;
  total: number;
}

export const getTemplateHistoryGroupList = async (): Promise<TemplatesElementsHistoryGroup[]> => {
  const { items } = await List<TemplatesElementsHistoryGroup>({
    name: 'TemplatesElementsHistoryGroup',
    settings: {
      offset: 0,
      limit: 100,
      order: [{ field: 'id_template_elements_history_group', type: 'DESC' }],
      filter: [
        { field: 'id_template', value: state.template?.id ?? 0, operator: OperatorType.Equals },
        { field: 'id_shop', value: UserState.activeShop?.id ?? 0, operator: OperatorType.Equals },
      ],
    },
    fields: ['id_template_elements_history_group', 'action_detail', 'sections_order_after', 'date_creation'],
  });
  return items;
};

export const getTemplateHistoryGroupListPaginated = async (
  offset = 0,
  limit = HISTORY_PAGE_SIZE,
): Promise<HistoryPaginationResult> => {
  const { items, total } = await List<TemplatesElementsHistoryGroup>({
    name: 'TemplatesElementsHistoryGroup',
    settings: {
      offset,
      limit,
      order: [{ field: 'id_template_elements_history_group', type: 'DESC' }],
      filter: [
        { field: 'id_template', value: state.template?.id ?? 0, operator: OperatorType.Equals },
        { field: 'id_shop', value: UserState.activeShop?.id ?? 0, operator: OperatorType.Equals },
      ],
    },
    fields: ['id_template_elements_history_group', 'action_detail', 'sections_order_after', 'date_creation'],
  });
  return {
    items,
    hasMore: offset + items.length < total,
    total,
  };
};

export const getClosestPointOfSave = (historyList: TemplatesElementsHistoryGroup[]) => historyList
  .findIndex((history: TemplatesElementsHistoryGroup) => history.action_detail === HistoryType.POINT_OF_SAVE);

export const getLastPointOfSaveId = (historyList: TemplatesElementsHistoryGroup[]) => {
  const historyItem = historyList
    .find((history: TemplatesElementsHistoryGroup) => history.action_detail === HistoryType.POINT_OF_SAVE);
  return historyItem ? historyItem.id_template_elements_history_group : 0;
};

export const getHistoryIndexFromGroupId = (historyList: TemplatesElementsHistoryGroup[], groupId: number) => historyList
  .findIndex((history: TemplatesElementsHistoryGroup) => history.id_template_elements_history_group === groupId);

export const getHistoryElements = async (historyGroupIds: string): Promise<TemplatesElementsHistory[]> => {
  const { items } = await List<TemplatesElementsHistory>({
    name: 'TemplatesElementsHistory',
    settings: {
      offset: 0,
      limit: 100,
      order: [{ field: 'id_template_elements_history', type: 'ASC' }],
      filter: [
        { field: 'id_template_elements_history_group', value: historyGroupIds, operator: OperatorType.In },
        { field: 'id_template', value: state.template?.id ?? 0, operator: OperatorType.Equals },
        { field: 'id_shop', value: UserState.activeShop?.id ?? 0, operator: OperatorType.Equals },
      ],
    },
    fields: ['id_template_elements_history', 'id_template_elements', 'html_after', 'css_after', 'translations_after'],
  });
  return items;
};

export const loadHistoryGroups = async (historyGroups: TemplatesElementsHistoryGroup[]) => {
  const ids = historyGroups.map((history) => {
    if (!history || !history.id_template_elements_history_group) {
      return false;
    }
    return history.id_template_elements_history_group;
  }).join(',');
  if (ids) {
    (await getHistoryElements(ids))
      .forEach((item: TemplatesElementsHistory) => {
        if (item.html_after && item.html_after.match(/data-spm-element-sync-id="([^"]+)"/g)) {
          store.commit('liveEditor/setForceUpdateSyncElements', true);
        }
        replaceSectionContent(item.id_template_elements, item.html_after ?? null, item.css_after ?? null, item.translations_after ?? null);
      });
  }
};

export const computeSectionsToSave = (historyType: HistoryType, activeSection: number | string | null, template: Template | null = null) => {
  const templateToCompute = template || state.template;
  // @TODO Vérifier si la liste est complète
  switch (historyType) {
    case HistoryType.POINT_OF_SAVE:
    case HistoryType.INTERMEDIATE_POINT_OF_SAVE:
    case HistoryType.TRANSLATIONS_CHANGE:
    case HistoryType.SECTIONS_ORDER_CHANGE:
    case HistoryType.WIDGET_ORDER_CHANGE:
    case HistoryType.EMBED_CHANGE_POSITION:
    case HistoryType.DISPLAY_CHANGE_URL:
    case HistoryType.LOAD_DESIGN:
    case HistoryType.LOAD_DESIGN_SYNC:
      return [...templateToCompute?.sections || []];
    case HistoryType.DESIGN_CHANGE:
    case HistoryType.ADVANCED_CSS_STATIC_CHANGE:
    case HistoryType.ADVANCED_CSS_STATIC_MOBILE_CHANGE:
      return [...templateToCompute?.sections.filter((current: Section) => current.id_template_elements.toString() === templateToCompute?.content.design.toString()) || []];
    default:
      return [...templateToCompute?.sections.filter((current: Section) => current.id_template_elements.toString() === activeSection?.toString()) || []];
  }
};

export const computeElementsToSave = (historyType: HistoryType): string[] => {
  // @TODO Vérifier si la liste est complète
  switch (historyType) {
    case HistoryType.DESIGN_CHANGE:
    case HistoryType.ADVANCED_CSS_STATIC_CHANGE:
    case HistoryType.ADVANCED_CSS_STATIC_MOBILE_CHANGE:
      return ['css'];
    case HistoryType.SECTIONS_ORDER_CHANGE:
      return ['html'];
    case HistoryType.TRANSLATIONS_CHANGE:
      return ['translations'];
    default:
      return ['html', 'css', 'translations'];
  }
};

/**
 * Create a new history point
 * @param historyType
 * @param checkCurrentHistoryId - if true, we check if we are on the latest history point or if the user has restored an old history point
 */
export const createHistory = async (historyType: HistoryType, checkFunnels = false) => {
  const isSavePoint = historyType === HistoryType.POINT_OF_SAVE
    || historyType === HistoryType.INTERMEDIATE_POINT_OF_SAVE;

  // For non-save-point types, mark the current template as having unsaved changes
  if (!isSavePoint && state.template) {
    templatesWithUnsavedChanges.add(state.template.id);
  }

  const executeCreateHistory = async (template: Template, fromIframe = true) => {
    // Skip history creation for save points if no changes were made
    if (isSavePoint && !templatesWithUnsavedChanges.has(template.id)) {
      return;
    }

    const activeSection = fromIframe
      ? (template.activeSection !== -1 ? template.activeSection : getLastActiveSection())
      : -1;
    const inputs: TemplatesElementsHistoryGroupInputItem[] = [];
    inputs.push({
      id_template: template.id,
      id_shop: UserState.activeShop?.id ?? 0,
      action_detail: historyType,
      sections_order_after: fromIframe ? JSON.stringify(getSectionIdsFromIframe()) : JSON.stringify(getSectionIdsFromTemplate(template)),
      date_creation: new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }),
      date_modification: new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }),
    });

    const {
      id, err,
    } = await Insert<TemplatesElementsHistoryGroupInputItem>({
      name: 'TemplatesElementsHistoryGroup',
      input: inputs,
      type: 'TemplatesElementsHistoryGroupInput',
    });

    const sectionstoSave: Section[] = computeSectionsToSave(historyType, activeSection, template);
    const elementstoSave: string[] = computeElementsToSave(historyType);

    if (err === '') {
      // Store last id of history group in template
      setTemplateConfigurationKeyInState('lastIdTemplateElementsHistoryGroup', +id, template);
      setTemplateConfigurationKeyInState('currentIdTemplateElementsHistoryGroup', +id, template);

      // Only a real save point is a rollback target: an intermediate one (test send) keeps the flag for the next save
      if (historyType === HistoryType.POINT_OF_SAVE) {
        templatesWithUnsavedChanges.delete(template.id);
      }

      // Save elements
      const items: TemplatesElementsHistoryInputItem[] = sectionstoSave.map((section: Section) => {
        if (typeof section.id_template_elements !== 'number') {
          // eslint-disable-next-line no-param-reassign
          section.id_template_elements = parseInt(section.id_template_elements, 10);
        }

        const item: TemplatesElementsHistoryInputItem = {
          id_template_elements_history_group: id,
          id_template_elements: section.id_template_elements,
          id_template: template.id,
          id_shop: UserState.activeShop?.id ?? 0,
          type: section.id_template_elements === template.content.design ? 'design' : 'section',
          date_creation: new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }),
          date_modification: new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }),
        };
        if (elementstoSave.includes('html')) item.html_after = section.html;
        if (elementstoSave.includes('css')) item.css_after = section.css;
        if (elementstoSave.includes('translations')) item.translations_after = section.translations;
        return item;
      }) ?? [];

      if (items.length) {
        await Insert<TemplatesElementsHistoryInputItem>({
          name: 'TemplatesElementsHistory',
          input: items,
          type: 'TemplatesElementsHistoryInput',
        });
      }
    }
  };
  if (state.template) {
    await executeCreateHistory(state.template as Template, true);
    if (checkFunnels && state.template && state.template.originTemplateId && state.templatesFromFunnel[state.template.originTemplateId]) {
      const templateInFunnels = state.templatesFromFunnel[state.template.originTemplateId].filter((t) => String(t.id) !== String(state.template?.id));
      // eslint-disable-next-line no-restricted-syntax
      for (const template of templateInFunnels) {
        // eslint-disable-next-line no-await-in-loop
        await executeCreateHistory(template as Template, false);
      }
    }
  }
};

/**
 * Checks if a save point is needed based on the current state.
 */
export const checkIfSavePointNeeded = async () => {
  const lastHistoryGroupId: number | null = state.template?.configuration.lastIdTemplateElementsHistoryGroup;
  const currentHistoryGroupId: number | null = state.template?.configuration.currentIdTemplateElementsHistoryGroup ?? lastHistoryGroupId;
  if ((lastHistoryGroupId !== currentHistoryGroupId)) {
    // We are not on the last history for this template, so first we create a new history point of save, then we save the action done by the user
    await createHistory(HistoryType.INTERMEDIATE_POINT_OF_SAVE);
  }
};

/**
 * Checks if a history type contains complete template state (all sections with all data)
 */
export const isCompleteHistoryType = (actionDetail: string): boolean => actionDetail === HistoryType.POINT_OF_SAVE;

/**
 * Progress callback type for history restoration
 */
export type HistoryRestoreProgressCallback = (current: number, total: number, actionDetail: string) => void;

/**
 * Restores template state to a specific history point.
 * This function handles both complete and partial history points:
 * 1. Finds the nearest complete history point (POINT_OF_SAVE) at or before the target index
 * 2. Loads that complete state first
 * 3. Then applies all partial changes in chronological order up to the target point
 *
 * @param historyList - The full list of history groups (sorted by date DESC, newest first)
 * @param targetIndex - The index of the history point to restore to
 * @param onProgress - Optional callback to report progress (current step, total steps, action detail)
 */
export const restoreToHistoryPoint = async (
  historyList: TemplatesElementsHistoryGroup[],
  targetIndex: number,
  onProgress?: HistoryRestoreProgressCallback,
) => {
  // Safety check: ensure historyList is valid and targetIndex is within bounds
  if (!historyList || historyList.length === 0 || targetIndex < 0 || targetIndex >= historyList.length) {
    console.warn('[History] restoreToHistoryPoint: invalid historyList or targetIndex', { historyListLength: historyList?.length, targetIndex });
    return;
  }

  // historyList is sorted DESC (newest first, index 0 = most recent)
  // We need to find the nearest POINT_OF_SAVE at or after targetIndex (older or equal)
  let completeHistoryIndex = -1;

  // Search from targetIndex towards the end (older entries) for a complete history point
  for (let i = targetIndex; i < historyList.length; i++) {
    const actionDetail = historyList[i].action_detail;
    if (actionDetail && isCompleteHistoryType(actionDetail)) {
      completeHistoryIndex = i;
      break;
    }
  }

  if (completeHistoryIndex === -1) {
    // No complete history point found, we can only load the target point directly
    // This is a fallback - ideally there should always be a POINT_OF_SAVE
    onProgress?.(1, 1, historyList[targetIndex].action_detail ?? '');
    await loadHistoryGroups([historyList[targetIndex]]);
    return;
  }

  if (completeHistoryIndex === targetIndex) {
    // Target is already a complete history point, just load it
    onProgress?.(1, 1, historyList[targetIndex].action_detail ?? '');
    await loadHistoryGroups([historyList[targetIndex]]);
    return;
  }

  // Calculate total steps: 1 for base state + partial changes count
  const partialChangesCount = completeHistoryIndex - targetIndex;
  const totalSteps = 1 + partialChangesCount;

  // First, load the complete history point (the base state)
  onProgress?.(1, totalSteps, historyList[completeHistoryIndex].action_detail ?? '');
  await loadHistoryGroups([historyList[completeHistoryIndex]]);

  // Then apply partial changes in chronological order (from older to newer)
  // We need to apply changes from (completeHistoryIndex - 1) down to targetIndex
  // Since the list is DESC, we iterate backwards through indices
  const partialHistoryGroups: TemplatesElementsHistoryGroup[] = [];
  for (let i = completeHistoryIndex - 1; i >= targetIndex; i--) {
    partialHistoryGroups.push(historyList[i]);
  }

  // partialHistoryGroups is already in chronological order (oldest first)
  // because the loop iterates from high index (older) to low index (newer) in the DESC-sorted list

  // Apply each partial change
  let currentStep = 2;
  for (const historyGroup of partialHistoryGroups) {
    onProgress?.(currentStep, totalSteps, historyGroup.action_detail ?? '');
    await loadHistoryGroups([historyGroup]);
    currentStep++;
  }
};
